import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { AuthService } from '@core/services/auth/auth.service';

/**
 * Guard to check if user has permission to access a specific module based on their permitted_modules
 */
export const ModulePermissionGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Comprehensive mapping between route paths and permitted module names
  // Strict submodule-level permission (Scenario 2 & 3) - no parent fallback
  const routeModuleMapping: { [key: string]: string } = {
    // Report routes
    'report/property': 'property_report',
    'report/scorecard': 'scorecard',
    'report/scheduler': 'report_scheduler',
    'report/data-ingestion': 'data_ingestion',

    // Brand.com routes
    'brand/channel-mix': 'channel_mix',
    'brand/visits-revenue': 'visits_and_revenue',
    'brand/source-traffic': 'source_traffic',
    'brand/domains': 'top_ref_domains',

    // Paid Media routes
    'media/overview': 'paid_media_overview',
    'media/portfolio-roll-up': 'portfolio_rollup',
    'media/meta-search': 'metasearch',
    'media/sponsored': 'sponsored_listing',
    'media/paid-search': 'search_ads',
    'media/paid-social': 'social_media_ads',
    'media/ota': 'ota_sponsored_listing',
    'media/display-ads': 'display_ads',

    // Social routes
    'social/facebook': 'social_facebook',

    // Reviews routes (strict submodule-level permission)
    'reviews/dashboard': 'reviews_dashboard',
    'reviews/responses': 'reviews_responses',
    'reviews/sentiment': 'reviews_sentiment',
    'reviews/review-feed': 'review_feed',

    // AI routes
    'ai/ai-report': 'ai_report',

    // User Management routes
    users: 'user',
    'users/create': 'user',
    'users/roles': 'role',
    // Region
    'users/regions': 'admin_region',
    'users/regions/edit': 'admin_region',
    'users/regions/add': 'admin_region',

    // Ownership Group
    'users/ownership-groups': 'admin_ownership_group',
    'users/ownership-groups/edit': 'admin_ownership_group',
    'users/ownership-groups/add': 'admin_ownership_group',
  };

  const user = authService.user;

  // If no user, let AuthGuard handle it (don't redirect here)
  if (!user) {
    return true;
  }

  // Allow access if user is superuser
  if (user.is_superuser) {
    return true;
  }

  // Build the full route path
  const fullPath = route.pathFromRoot
    .filter((r) => r.routeConfig?.path)
    .map((r) => r.routeConfig!.path)
    .join('/');

  // Check if this route requires permissions
  const requiredModules = routeModuleMapping[fullPath];

  // If no mapping exists, allow access (for non-protected routes like /profile)
  if (!requiredModules) {
    return true;
  }

  // If no permitted_modules or empty array, block access and redirect to home
  if (!user.permitted_modules || user.permitted_modules.length === 0) {
    router.navigate(['/'], { replaceUrl: true });
    return false;
  }

  // Check if user has the required module permission (strict check - no parent fallback)
  let hasPermission = user.permitted_modules.includes(requiredModules as string);

  // Special case: report_scheduler is merged with property_report in backend
  // If property_report is permitted, report_scheduler should also be allowed
  if (!hasPermission && requiredModules === 'report_scheduler') {
    hasPermission = user.permitted_modules.includes('property_report');
  }

  if (hasPermission) {
    return true;
  }

  // User doesn't have permission, redirect to first permitted module
  const firstModule = user.permitted_modules[0];
  const redirectPath = getRedirectPathForModule(firstModule);

  router.navigate([redirectPath], { replaceUrl: true });
  return false;
};

/**
 * Helper function to get the redirect path based on module name
 */
function getRedirectPathForModule(moduleName: string): string {
  const modulePathMapping: { [key: string]: string } = {
    // Report
    property_report: '/report/property',
    scorecard: '/report/scorecard',
    report_scheduler: '/report/scheduler',
    data_ingestion: '/report/data-ingestion',

    // Brand.com
    brand_dot_com: '/brand/channel-mix',
    channel_mix: '/brand/channel-mix',
    visits_and_revenue: '/brand/visits-revenue',
    source_traffic: '/brand/source-traffic',
    top_ref_domains: '/brand/domains',

    // Paid Media
    paid_media: '/media/overview',
    paid_media_overview: '/media/overview',
    portfolio_rollup: '/media/portfolio-roll-up',
    metasearch: '/media/meta-search',
    sponsored_listing: '/media/sponsored',
    search_ads: '/media/paid-search',
    social_media_ads: '/media/paid-social',
    ota_sponsored_listing: '/media/ota',
    display_ads: '/media/display-ads',

    // Social
    social: '/social/facebook',
    social_facebook: '/social/facebook',

    // Reviews
    reviews: '/reviews/dashboard',
    reviews_dashboard: '/reviews/dashboard',
    reviews_responses: '/reviews/responses',
    reviews_sentiment: '/reviews/sentiment',
    review_feed: '/reviews/review-feed',

    // AI
    ai_report: '/ai/ai-report',

    // User
    user: '/users',
    role: '/users/roles',
    admin_region: '/regions',
    'admin_ownership-group': '/ownership-groups',
  };

  return modulePathMapping[moduleName] || '/';
}
